
import { CreationRecord, ExplodedScheme, PointTransaction, User } from "../types";

const DB_NAME = "AICreatorDB";
const STORE_NAME = "records";
const SCHEME_STORE = "schemes";
const POINTS_STORE = "points";
const USERS_KEY = "ai_creator_users";
const CURRENT_USER_KEY = "ai_creator_current_user";
const DB_VERSION = 4; // Bump version for index
const BASE_API_URL = (import.meta as any).env?.VITE_APP_PREFIX || '/api';

export const dbService = {
  normalizeImageUrl(url: string | null | undefined): string {
    if (!url) return "";
    if (url.startsWith('http') || url.startsWith('data:')) return url;
    // Determine environment: pro vs others
    const baseUrl = (import.meta as any).env?.VITE_OSS_ENDPOINT || 'https://static.rapiddirect.com';
    const path = url.startsWith('/') ? url : `/${url}`;
    return `${baseUrl}${path}`;
  },

  open(): Promise<IDBDatabase> {
    return Promise.resolve(null)
  },

  async _fetch(endpoint: string, options: RequestInit = {}): Promise<any> {
    const token = localStorage.getItem('auth_token') || this.getCookie('auth');
    const apiAuth = localStorage.getItem('api_auth') || '{{ApiAuth}}';
    
    const url = `${BASE_API_URL}${endpoint}`;
    
    const headers: any = {
      'auth': token || '',
      'apiAuth': apiAuth,
      ...options.headers
    };

    if (options.body && !(options.body instanceof FormData) && !headers['Content-Type'] && !(options.body instanceof URLSearchParams)) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(url, { ...options, headers });
    const result = await response.json();
    return this.handleResponse(result);
  },

  // Auth Methods
  async register(userData: Omit<User, 'id'> & { password?: string }): Promise<User> {
    const payload: any = {
      accounts: userData.email,
      password: userData.password,
      type: "e",
      code: "",
      user: userData.lastName || userData.firstName || "User",
      userName: userData.firstName || "User",
      nvc_val: "",
      invite_code: "",
      time_zone: 8,
      rdtm_from: "",
      rdtm_keyword: "",
      rdtm_other: "",
      rdtm_from_create_time: 0,
      reg_type: 2
    };

    const body = new URLSearchParams();
    Object.entries(payload).forEach(([key, value]) => {
      body.append(key, value !== null ? String(value) : "");
    });

    // Use local proxy to bypass Mixed Content
    const result = await this._fetch(`/5cda73342650d`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: body
    });
    if (result.code !== 1) {
      throw new Error(result.msg || "Registration failed");
    }

    return this.login(userData.email, userData.password);
  },

  async login(email: string, password?: string): Promise<User> {
    const body = new URLSearchParams();
    body.append('email', email);
    body.append('password', password || "");

    // Use local proxy to bypass Mixed Content
    const result = await this._fetch(`/5ccfd6629449a`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: body.toString()
    });
    if (result.code !== 1) {
      throw new Error(result.msg || "Invalid email or password");
    }

    const data = result.data;
    const user: User = {
      id: data.address_id.toString(),
      email: email,
      firstName: data.name,
      lastName: "",
      remaining_ai_token: data.remaining_ai_token
    };

    // Set cookie auth={{token}} for Iframe compatibility
    document.cookie = `auth=${data.token}; path=/; max-age=86400; SameSite=None; Secure`;
    localStorage.setItem('auth_token', data.token);

    this.setCurrentUser(user);
    return user;
  },

  setCurrentUser(user: User | null) {
    if (user) {
      localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(CURRENT_USER_KEY);
    }
  },

  getCurrentUser(): User | null {
    const user = localStorage.getItem(CURRENT_USER_KEY);
    return user ? JSON.parse(user) : null;
  },

  logout() {
    this.setCurrentUser(null);
    localStorage.removeItem('auth_token');
    document.cookie = "auth=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=None; Secure";
  },

  handleResponse(result: any) {
    if (result && result.code === -23) {
      this.logout();
      window.dispatchEvent(new CustomEvent('unauthorized_access'));
      throw new Error(result.msg || "Please log in");
    }
    return result;
  },

  // 保存完整数据到本地DB储存
  async saveRecord(record: CreationRecord): Promise<void> {
    const user = this.getCurrentUser();
    if (user && !record.userId) record.userId = user.id;
    
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(record);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  },

  async saveTransaction(tx: PointTransaction): Promise<void> {
    const user = this.getCurrentUser();
    if (user && !tx.userId) tx.userId = user.id;

    const db = await this.open();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(POINTS_STORE, "readwrite");
      const store = transaction.objectStore(POINTS_STORE);
      const request = store.put(tx);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  },

  async getTransactions(): Promise<PointTransaction[]> {
    const user = this.getCurrentUser();
    if (!user) return [];

    const db = await this.open();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(POINTS_STORE, "readonly");
      const store = transaction.objectStore(POINTS_STORE);
      const index = store.index("userId");
      const request = index.getAll(user.id);
      request.onsuccess = () => {
        const result = request.result as PointTransaction[];
        resolve(result.sort((a, b) => b.timestamp - a.timestamp));
      };
      request.onerror = () => reject(request.error);
    });
  },

  async saveScheme(scheme: ExplodedScheme & { userId?: string }): Promise<void> {
    const user = this.getCurrentUser();
    if (user && !scheme.userId) scheme.userId = user.id;

    const db = await this.open();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(SCHEME_STORE, "readwrite");
      const store = transaction.objectStore(SCHEME_STORE);
      const request = store.put(scheme);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  },

  async getAllSchemes(): Promise<ExplodedScheme[]> {
    const user = this.getCurrentUser();
    if (!user) return [];

    const db = await this.open();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(SCHEME_STORE, "readonly");
      const store = transaction.objectStore(SCHEME_STORE);
      const index = store.index("userId");
      const request = index.getAll(user.id);
      request.onsuccess = () => {
        const result = request.result as ExplodedScheme[];
        resolve(result.sort((a, b) => b.timestamp - a.timestamp));
      };
      request.onerror = () => reject(request.error);
    });
  },

  async getAllRecords(): Promise<CreationRecord[]> {
    const user = this.getCurrentUser();
    if (!user) return [];

    const db = await this.open();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index("userId");
      const request = index.getAll(user.id);
      request.onsuccess = () => {
        const result = request.result as CreationRecord[];
        resolve(result.sort((a, b) => b.timestamp - a.timestamp));
      };
      request.onerror = () => reject(request.error);
    });
  },

  async getRecord(id: string): Promise<CreationRecord | null> {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  },

  async createProject(params: any): Promise<{ project_id: string; project_no: string; project_name: string }> {
    const result = await this._fetch(`/699fb545b84cd`, {
      method: 'POST',
      body: JSON.stringify(params)
    });

    if (result.code !== 1) {
      throw new Error(result.msg || "Failed to create project");
    }

    return result.data;
  },

  async getRemoteProjects(page: number = 1, size: number = 10): Promise<{
    total: number;
    per_page: string;
    current_page: number;
    last_page: number;
    data: any[];
  }> {
    const result = await this._fetch(`/699fb765097ab?page=${page}&size=${size}`);
    if (result.code !== 1) {
      throw new Error(result.msg || "Failed to fetch projects");
    }

    return result.data;
  },

  async getRemoteProjectDetails(projectId: number): Promise<any> {
    const result = await this._fetch(`/69a01905ae78e?project_id=${projectId}`);
    if (result.code !== 1) {
      throw new Error(result.msg || "Failed to fetch project details");
    }
    if(result.data?.images_data && Array.isArray(result.data.images_data)) {
      result.data.images_data = result.data.images_data.map((p: any) => this.normalizeImageUrl(p));
    }
    if(result.data?.ver_images && Array.isArray(result.data.ver_images)) {
      //按版本号排序，然后转换为二维数组，只保留处理后的图片URL
      result.data.ver_images = result.data.ver_images
        .sort((a: any, b: any) => (a.version || 0) - (b.version || 0))
        .map((p: any) => {
          if (p.images_list && Array.isArray(p.images_list)) {
            return p.images_list.map((item: any) => this.normalizeImageUrl(item));
          }
          return [];
        });
    }

    return result.data;
  },

  async saveChatMessage(payload: any): Promise<void> {
    const result = await this._fetch(`/699fef19e0e35`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    if (result.code !== 1) {
      console.error("Failed to save chat message", result.msg);
    }
  },

  // 获取用户积分
  async getUserPoints(): Promise<any> {
    const result = await this._fetch(`/699e99c913bf9`);

    if (result.code !== 1) {
      console.error("Failed to get user points", result.msg);
    }
    return result.data;
  },

  getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
    return null;
  },

  async validateSession(): Promise<User | null> {
    const token = this.getCookie('auth');
    console.log("🚀 ~ token:", token)
    if (!token) return null;

    // Ensure localStorage is in sync
    localStorage.setItem('auth_token', token);

    try {
      const data = await this.getUserPoints();
      if (data) {
        const user: User = {
          id: data.address_id?.toString() || "",
          email: data.email || "",
          firstName: data.user_name || "User",
          lastName: "",
          remaining_ai_token: data.remaining_ai_token
        };
        this.setCurrentUser(user);
        return user;
      }
    } catch (e) {
      console.error("Session validation failed", e);
    }
    return null;
  },

  // 上传文件
  async uploadFile(file: File): Promise<any> {
    // 确保 file 是有效的
    if (!(file instanceof File)) {
      console.error("uploadFile: Argument is not a File object", file);
    }

    const formData = new FormData();
    formData.append('file', file)            // 关键：直接传 File 对象
    formData.append('type', 'image');
    
    const result = await this._fetch(`/5d15ed1791c1c`, {
      method: 'POST',
      body: formData
    });
    return result;
  },

  // 保存项目图片
  async saveProjectImages(projectId: number, imagesData: string[], totalAmount?: number, customPrompt?: string): Promise<any> {
    const result = await this._fetch(`/69a0ff8137194`, {
      method: 'POST',
      body: JSON.stringify({
        project_id: projectId,
        images_data: imagesData,
        custom_prompt: customPrompt || '',
        total_amount: totalAmount
      })
    });
    return result;
  },

  async requestEngineeringEvaluation(payload: any): Promise<any> {
    return await this._fetch(`/69a4e2f4365cc`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },

  // 切换项目图片版本
  async switchProjectImageVersion(projectId: number, version: number): Promise<any> {
    const result = await this._fetch(`/69a5014fdafd7`, {
      method: 'POST',
      body: JSON.stringify({
        project_id: projectId,
        version: version
      })
    });
    return result;
  },

  // 删除项目
  async deleteProject(projectId: string): Promise<any> {
    const result = await this._fetch(`/69a7e21a443cc`, {
      method: 'POST',
      body: JSON.stringify({
        project_id: projectId
      })
    });
    return result;
  },

  // 获取点数消耗明细
  async getPointUsageHistory(page: number = 1, size: number = 10): Promise<{
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
    data: any[];
  }> {
    const result = await this._fetch(`/69a7da2eb7a49?page=${page}&size=${size}`);

    if (result.code !== 1) {
      throw new Error(result.msg || "Failed to fetch point history");
    }

    return result.data;
  },
};
